import { useState, useEffect, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Shield, Check, X } from 'lucide-react';
import { motion } from 'framer-motion';
import { supabase } from '@/integrations/supabase/client';
import loginHero from '@/assets/login-hero.jpg';
import { useNavigate } from 'react-router-dom';

function getPasswordStrength(pwd: string) {
  const checks = {
    length: pwd.length >= 8,
    upper: /[A-Z]/.test(pwd),
    lower: /[a-z]/.test(pwd),
    number: /[0-9]/.test(pwd),
    special: /[^A-Za-z0-9]/.test(pwd),
  };
  const passed = Object.values(checks).filter(Boolean).length;
  return { checks, passed, strong: passed >= 4 && checks.length };
}

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [ready, setReady] = useState(false);

  const strength = useMemo(() => getPasswordStrength(password), [password]);

  useEffect(() => {
    // Listen for the PASSWORD_RECOVERY event from Supabase
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY') {
        setReady(true);
      }
    });
    return () => subscription.unsubscribe();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!strength.strong) {
      setError('Password does not meet strength requirements');
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match');
      return;
    }
    setLoading(true);
    try {
      const { error: updateError } = await supabase.auth.updateUser({ password });
      if (updateError) throw updateError;
      setSuccess(true);
      setTimeout(() => navigate('/'), 2000);
    } catch (err: any) {
      setError(err?.message || 'Failed to update password');
    } finally {
      setLoading(false);
    }
  };

  const Rule = ({ met, label }: { met: boolean; label: string }) => (
    <div className="flex items-center gap-1.5 text-xs">
      {met ? <Check className="h-3 w-3 text-primary" /> : <X className="h-3 w-3 text-muted-foreground" />}
      <span className={met ? 'text-foreground' : 'text-muted-foreground'}>{label}</span>
    </div>
  );

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4 relative overflow-hidden">
      {/* Hero background image */}
      <div className="absolute inset-0 z-0">
        <img src={loginHero} alt="" className="h-full w-full object-cover object-top opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-b from-background/30 via-background/70 to-background" />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-sm relative z-10"
      >
        <div className="mb-8 text-center">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-primary/10">
            <Shield className="h-8 w-8 text-primary" />
          </div>
          <h1 className="font-display text-3xl font-bold text-foreground">Reset Password</h1>
          <p className="mt-1 text-muted-foreground">Choose a new master password</p>
        </div>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">New password</CardTitle>
            <CardDescription>
              {ready
                ? 'Enter your new password below. This will re-encrypt your local data.'
                : 'Verifying your reset link…'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {success ? (
              <p className="text-sm text-primary font-medium">Password updated! Redirecting…</p>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-3">
                <Input
                  type="password"
                  placeholder="New password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  className="h-12 text-lg"
                  disabled={!ready}
                  autoFocus
                />

                {password.length > 0 && (
                  <div className="grid grid-cols-2 gap-1 rounded-lg bg-muted/50 p-2.5">
                    <Rule met={strength.checks.length} label="8+ characters" />
                    <Rule met={strength.checks.upper} label="Uppercase" />
                    <Rule met={strength.checks.lower} label="Lowercase" />
                    <Rule met={strength.checks.number} label="Number" />
                    <Rule met={strength.checks.special} label="Special char" />
                  </div>
                )}

                <Input
                  type="password"
                  placeholder="Confirm new password"
                  value={confirm}
                  onChange={e => setConfirm(e.target.value)}
                  className="h-12 text-lg"
                  disabled={!ready}
                />

                {error && <p className="text-sm text-destructive">{error}</p>}

                <Button
                  type="submit"
                  className="h-12 w-full text-base"
                  disabled={loading || !ready || !strength.strong}
                >
                  {loading ? 'Updating…' : 'Update Password'}
                </Button>
              </form>
            )}
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
}
